import * as React from 'react';
import { Loader } from 'Src/components/Loader/Loader';
import {
  getAllSuperAdminSchoolSports,
  setSchoolSportFavorite,
  SuperAdminSport
} from 'Src/helpers/service/superadmin/sports';

interface Props {
  schoolId: string;
}

interface State {
  isLoading: boolean;
  isSaving: string | null;
  sports: SuperAdminSport[];
  errorMessage: string;
  filter: string;
}

export class SuperAdminSchoolSports extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: false,
      isSaving: null,
      sports: [],
      errorMessage: '',
      filter: ''
    };
  }

  componentDidMount() {
    this.load();
  }

  load = () => {
    this.setState({ isLoading: true, errorMessage: '' });
    getAllSuperAdminSchoolSports(this.props.schoolId)
      .then(sports => {
        this.setState({ isLoading: false, sports });
      })
      .catch(err => {
        console.error('Cannot load school sports', err);
        this.setState({ isLoading: false, errorMessage: 'Could not load sports.' });
      });
  };

  onToggleFavorite = (sport: SuperAdminSport) => {
    const isFavorite = !(sport as any).isFavorite;
    this.setState({ isSaving: sport.id, errorMessage: '' });

    setSchoolSportFavorite(this.props.schoolId, sport.id, isFavorite)
      .then(updated => {
        this.setState(prev => ({
          isSaving: null,
          sports: prev.sports.map(s => (s.id === updated.id ? updated : s))
        }));
      })
      .catch(err => {
        console.error('Cannot update favorite', err);
        this.setState({ isSaving: null, errorMessage: 'Could not update sport. Please try again.' });
      });
  };

  render() {
    const { isLoading, isSaving, sports, errorMessage, filter } = this.state;

    if (isLoading) return <Loader />;

    const filtered = filter ? sports.filter(s => (s.name || '').toLowerCase().includes(filter.toLowerCase())) : sports;

    const favorites = filtered.filter(s => (s as any).isFavorite);
    const others = filtered.filter(s => !(s as any).isFavorite);

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-1">Sports</h4>
            <div className="text-muted small">
              Favourite sports are highlighted. Total: {sports.length}, favourites: {favorites.length}.
            </div>
          </div>
          <button className="btn btn-outline-secondary btn-sm" onClick={this.load} disabled={isSaving !== null}>
            Refresh
          </button>
        </div>

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

        <div className="mb-3">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Filter by name…"
            value={filter}
            onChange={e => this.setState({ filter: e.target.value })}
            style={{ maxWidth: 260 }}
          />
        </div>

        {filtered.length === 0 && <div className="text-muted">No sports found.</div>}

        {filtered.length > 0 && (
          <table className="table table-sm table-bordered">
            <thead className="thead-light">
              <tr>
                <th>Name</th>
                <th style={{ width: 120 }}>Favourite</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {[...favorites, ...others].map(sport => {
                const isFav = !!(sport as any).isFavorite;
                const saving = isSaving === sport.id;
                return (
                  <tr key={sport.id} className={isFav ? 'table-warning' : ''}>
                    <td>
                      <span>{sport.name || '—'}</span>
                      {sport.description && <div className="text-muted small">{sport.description}</div>}
                    </td>
                    <td>
                      {isFav ? (
                        <span className="badge badge-warning">⭐ Favourite</span>
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                    </td>
                    <td>
                      <button
                        className={`btn btn-sm ${isFav ? 'btn-outline-secondary' : 'btn-outline-warning'}`}
                        onClick={() => this.onToggleFavorite(sport)}
                        disabled={saving || isSaving !== null}
                      >
                        {saving ? '…' : isFav ? 'Remove' : 'Add fav'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  }
}
